import { tx } from "../lib/i18n";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, BookOpen, UserCog, Wallet, Plus, Upload, Video, XCircle, Clock, AlertTriangle, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, photoUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useMeta } from "../lib/useMeta";
import { useAuth } from "../context/AuthContext";
import { fmtFull, fmtTime, money, weekdayNames } from "../lib/format";
import { DashShell, Stat, Badge, Table, Section, useTab } from "../components/dash/DashKit";
import { Spinner } from "../components/site/Layout";
import { Avatar } from "../components/site/Placeholder";
import { TeacherProfileFields } from "./TeacherApply";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const openable = (s) => new Date(s.start) - Date.now() <= 10 * 60000 && new Date(s.end) > Date.now();

function SessionCard({ s, reload, lang }) {
  const cancel = async () => {
    if (!window.confirm(tx("Deze les annuleren? Alle ingeschreven ouders ontvangen automatisch lestegoed."))) return;
    try { await api.post(`/teacher/sessions/${s.id}/cancel`); toast.success(tx("Les geannuleerd")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="osk-card p-5" data-testid={`teacher-session-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-extrabold">{s.lesson?.title}</p>
          <p className="text-sm text-osk-text">{fmtFull(s.start, lang)} – {fmtTime(s.end)} · {s.seats_taken}/{s.lesson?.max_children} {tx("plekken bezet")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge status={s.status} session />
          {s.status === "scheduled" && (openable(s)
            ? <Link to={`/docent/les/${s.id}`} className="osk-btn-primary px-4 py-2 text-sm" data-testid={`open-room-${s.id}`}><Video className="h-4 w-4" />{tx("Les starten")}</Link>
            : new Date(s.start) > Date.now() && <>
              <button disabled className="osk-btn-primary px-4 py-2 text-sm" data-testid={`open-room-disabled-${s.id}`}><Video className="h-4 w-4" />{tx("Les starten")}</button>
              <button onClick={cancel} className="text-osk-muted hover:text-osk-red" title={tx("Annuleren")} data-testid={`teacher-cancel-session-${s.id}`}><XCircle className="h-5 w-5" /></button>
            </>)}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {s.children.map((c) => (
          <span key={c.id} className="osk-pill bg-osk-light text-osk-navy" data-testid={`enrolled-child-${c.id}`}>
            {c.name} ({c.grade}){c.attendance && <span className="ml-1 text-emerald-700">· {Math.round(c.attendance.minutes)} min</span>}
          </span>
        ))}
        {!s.children.length && <span className="text-xs text-osk-muted">{tx("Nog geen ingeschreven kinderen")}</span>}
      </div>
    </div>
  );
}

function Sessions({ lang }) {
  const [scope, setScope] = useState("upcoming");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/teacher/sessions", { params: { scope } }).then((r) => setRows(r.data)), [scope]);
  useEffect(() => { setRows(null); load(); }, [load]);
  return (
    <Section title={tx("Lessen")} action={
      <div className="flex rounded-full bg-white p-1 shadow-sm">
        {[["upcoming", tx("Komend")], ["past", tx("Afgelopen")]].map(([k, l]) => <button key={k} onClick={() => setScope(k)} data-testid={`sessions-scope-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-bold ${scope === k ? "bg-osk-blue text-white" : "text-osk-navy"}`}>{l}</button>)}
      </div>}>
      {!rows ? <Spinner /> : <div className="space-y-3">{rows.map((s) => <SessionCard key={s.id} s={s} reload={load} lang={lang} />)}
        {!rows.length && <div className="osk-card p-8 text-center text-osk-muted">{tx("Geen lessen.")}</div>}</div>}
    </Section>
  );
}

const EMPTY = { title: "", description: "", subject: "Wiskunde", school_type: "HAVO", grade: "Klas 1", language: "Nederlands", format: "Kleine groep", max_children: 6, price: 35,
  island: "aruba", schedule_type: "weekly", schedule: { date: "", time: "19:00", duration: 60, weekday: 0, start_date: "", dates: [{ date: "", time: "10:00" }, { date: "", time: "10:00" }] } };

function LessonForm({ initial, onDone }) {
  const meta = useMeta();
  const { lang } = useI18n();
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setS = (k, v) => setF({ ...f, schedule: { ...f.schedule, [k]: v } });
  const max = meta?.format_max?.[f.format] || 1;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, max_children: Number(f.max_children), price: Number(f.price), schedule: { ...f.schedule, duration: Number(f.schedule.duration), weekday: Number(f.schedule.weekday) } };
    try {
      initial ? await api.put(`/teacher/lessons/${initial.id}`, body) : await api.post("/teacher/lessons", body);
      toast.success(tx("Les opgeslagen")); onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const Sel = ({ k, opts, label }) => (
    <label className="block"><span className="osk-label">{label}</span>
      <select className="osk-input" value={f[k]} data-testid={`lesson-${k}-select`} onChange={(e) => {
        const v = e.target.value;
        if (k === "school_type") return setF({ ...f, school_type: v, grade: meta.grades[v][0] });
        if (k === "format") return setF({ ...f, format: v, max_children: Math.min(f.max_children, meta.format_max[v]) });
        set(k, v);
      }}>{opts.map((o) => <option key={o} value={o}>{tx(o)}</option>)}</select>
    </label>
  );
  if (!meta) return <Spinner />;
  return (
    <form onSubmit={save} className="space-y-4" data-testid="lesson-form">
      <label className="block"><span className="osk-label">{tx("Titel")}</span><input className="osk-input" required value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="lesson-title-input" /></label>
      <label className="block"><span className="osk-label">{tx("Beschrijving")}</span><textarea className="osk-input min-h-[90px]" required value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="lesson-description-input" /></label>
      <p className="flex items-center gap-2 text-xs text-osk-muted"><AlertTriangle className="h-4 w-4 text-osk-orange" />{tx("Telefoonnummers, e-mailadressen, links en social media zijn niet toegestaan.")}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Sel k="subject" opts={meta.subjects} label={tx("Vak")} />
        <Sel k="school_type" opts={meta.school_types} label={tx("Schooltype")} />
        <Sel k="grade" opts={meta.grades[f.school_type]} label={tx("Klas / leerjaar")} />
        <Sel k="language" opts={meta.languages} label={tx("Lestaal")} />
        <Sel k="format" opts={meta.formats} label={tx("Lesvorm")} />
        <label className="block"><span className="osk-label">{tx("Max. kinderen (max {n})", { n: max })}</span><input type="number" min="1" max={max} className="osk-input" value={f.max_children} onChange={(e) => set("max_children", e.target.value)} data-testid="lesson-max-children-input" /></label>
        <label className="block"><span className="osk-label">{tx("Prijs per kind per les (AWG)")}</span><input type="number" min="1" step="0.01" className="osk-input" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="lesson-price-input" /></label>
        <label className="block"><span className="osk-label">{tx("Duur (minuten)")}</span><input type="number" min="15" className="osk-input" value={f.schedule.duration} onChange={(e) => setS("duration", e.target.value)} data-testid="lesson-duration-input" /></label>
      </div>
      <div>
        <span className="osk-label">{tx("Roostertype")}</span>
        <div className="grid gap-2 sm:grid-cols-3">
          {[["single", tx("Losse les")], ["weekly", tx("Wekelijkse reeks (per maand)")], ["block", tx("Cursusblok (pakket)")]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => set("schedule_type", k)} data-testid={`schedule-type-${k}`}
              className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold ${f.schedule_type === k ? "border-osk-blue bg-osk-light" : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {f.schedule_type === "single" && (
        <div className="grid gap-4 sm:grid-cols-2">
          <input type="date" className="osk-input" required value={f.schedule.date} onChange={(e) => setS("date", e.target.value)} data-testid="schedule-date-input" />
          <input type="time" className="osk-input" required value={f.schedule.time} onChange={(e) => setS("time", e.target.value)} data-testid="schedule-time-input" />
        </div>
      )}
      {f.schedule_type === "weekly" && (
        <div className="grid gap-4 sm:grid-cols-3">
          <select className="osk-input" value={f.schedule.weekday} onChange={(e) => setS("weekday", e.target.value)} data-testid="schedule-weekday-select">
            {weekdayNames(lang).map((d, i) => <option key={d} value={(i + 0) % 7 === 6 ? 6 : i}>{d}</option>)}
          </select>
          <input type="time" className="osk-input" required value={f.schedule.time} onChange={(e) => setS("time", e.target.value)} data-testid="schedule-time-input" />
          <input type="date" className="osk-input" required value={f.schedule.start_date} onChange={(e) => setS("start_date", e.target.value)} data-testid="schedule-start-date-input" />
        </div>
      )}
      {f.schedule_type === "block" && (
        <div className="space-y-2">
          {f.schedule.dates.map((d, i) => (
            <div key={i} className="flex gap-2">
              <input type="date" className="osk-input" required value={d.date} data-testid={`block-date-${i}`} onChange={(e) => setS("dates", f.schedule.dates.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
              <input type="time" className="osk-input" required value={d.time} data-testid={`block-time-${i}`} onChange={(e) => setS("dates", f.schedule.dates.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} />
              {f.schedule.dates.length > 2 && <button type="button" onClick={() => setS("dates", f.schedule.dates.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-osk-muted" /></button>}
            </div>
          ))}
          <button type="button" className="text-sm font-bold text-osk-blue" onClick={() => setS("dates", [...f.schedule.dates, { date: "", time: f.schedule.dates[0].time }])} data-testid="block-add-date-btn">{tx("+ Datum toevoegen")}</button>
        </div>
      )}
      <p className="text-xs text-osk-muted">{tx("Tijden in Aruba-tijd (AST, UTC−4).")}</p>
      <button className="osk-btn-primary w-full" disabled={busy} data-testid="lesson-save-btn">{tx("Les opslaan")}</button>
    </form>
  );
}

function Lessons({ approved }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/teacher/lessons").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/teacher/lessons/${id}/materials`, fd); toast.success(tx("Materiaal geüpload")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (l) => { await api.post(`/teacher/lessons/${l.id}/status`, { status: l.status === "active" ? "archived" : "active" }); load(); };
  if (!rows) return <Spinner />;
  return (
    <Section title={tx("Mijn lessen")} action={approved && <button className="osk-btn-primary px-5 py-2 text-sm" onClick={() => setEdit("new")} data-testid="new-lesson-btn"><Plus className="h-4 w-4" />{tx("Nieuwe les")}</button>}>
      {!approved && <div className="osk-card mb-4 bg-tile-yellow p-4 text-sm font-semibold" data-testid="not-approved-notice">{tx("Je kunt lessen publiceren zodra je aanmelding is goedgekeurd door ons team.")}</div>}
      <div className="space-y-3">
        {rows.map((l) => (
          <div key={l.id} className="osk-card p-5" data-testid={`teacher-lesson-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-extrabold">{l.title}</p>
                <p className="text-sm text-osk-text">{l.subject} · {l.school_type} {l.grade} · {tx(l.format)} · {money(l.price, l.currency)} · {l.upcoming} {tx(l.upcoming === 1 ? "komende les" : "komende lessen")}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge status={l.status} />
                <button className="text-sm font-bold text-osk-blue" onClick={() => setEdit(l)} data-testid={`edit-lesson-${l.id}`}>{tx("Bewerken")}</button>
                <button className="text-sm font-bold text-osk-muted" onClick={() => toggle(l)} data-testid={`toggle-lesson-${l.id}`}>{l.status === "active" ? tx("Archiveren") : tx("Activeren")}</button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {l.material_files.map((m) => <span key={m.id} className="osk-pill bg-osk-light text-osk-navy"><FileText className="mr-1 h-3.5 w-3.5" />{m.filename}</span>)}
              <label className="osk-pill cursor-pointer bg-white text-osk-blue ring-1 ring-osk-blue/30"><Upload className="mr-1 h-3.5 w-3.5" />{tx("Materiaal")}
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(l.id, e.target.files[0])} data-testid={`upload-material-${l.id}`} /></label>
            </div>
          </div>
        ))}
        {!rows.length && <div className="osk-card p-8 text-center text-osk-muted">{tx("Nog geen lessen.")}</div>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle>{edit === "new" ? tx("Nieuwe les") : tx("Les bewerken")}</DialogTitle></DialogHeader>
          {edit && <LessonForm initial={edit === "new" ? null : edit} onDone={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

const DOCS = [["id", "Identiteitsbewijs"], ["diploma", "Diploma('s)"], ["good_conduct", "Verklaring omtrent gedrag"]];

function Profile({ refresh }) {
  const meta = useMeta();
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get("/teacher/profile").then((r) => {
    setP(r.data);
    const d = r.data;
    setForm({ display_name: d.display_name, bio: d.bio, subjects: d.subjects, school_types: d.school_types, languages: d.languages, island: d.island,
      bank_name: d.bank?.name || "", bank_account: d.bank?.account || "", bank_holder: d.bank?.holder || "" });
  });
  useEffect(() => { load(); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.put("/teacher/profile", form); toast.success(tx("Profiel opgeslagen")); } catch (err) { toast.error(errMsg(err)); } };
  const up = async (path, file, extra = {}) => {
    const fd = new FormData(); fd.append("file", file); Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    try { await api.post(path, fd); toast.success(tx("Geüpload")); load(); refresh?.(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!p || !form) return <Spinner />;
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <form onSubmit={save} className="osk-card space-y-5 p-6" data-testid="teacher-profile-form">
        <TeacherProfileFields form={form} setForm={setForm} meta={meta} />
        <button className="osk-btn-primary" data-testid="teacher-profile-save-btn">{tx("Profiel opslaan")}</button>
      </form>
      <div className="space-y-6">
        <div className="osk-card flex flex-col items-center gap-3 p-6 text-center">
          <Avatar fileId={p.photo_file_id} size={110} testid="teacher-photo" />
          <label className="osk-btn-outline cursor-pointer py-2 text-sm"><Upload className="h-4 w-4" />Profielfoto
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && up("/teacher/photo", e.target.files[0])} data-testid="teacher-photo-upload" /></label>
        </div>
        <div className="osk-card space-y-3 p-6" data-testid="teacher-documents">
          <h3 className="text-lg">{tx("Documenten")}</h3>
          <p className="text-xs text-osk-muted">{tx("Alleen zichtbaar voor het Onskola-team.")}</p>
          {DOCS.map(([k, l]) => {
            const docs = p.documents.filter((d) => d.doc_type === k);
            return (
              <div key={k} className="flex items-center justify-between gap-2 rounded-xl bg-[#F7FAFE] p-3">
                <div className="min-w-0"><p className="text-sm font-bold">{tx(l)}</p><p className="truncate text-xs text-osk-muted">{docs.length ? docs.map((d) => d.filename).join(", ") : tx("Nog niet geüpload")}</p></div>
                <label className="cursor-pointer text-osk-blue"><Upload className="h-5 w-5" />
                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && up("/teacher/documents", e.target.files[0], { doc_type: k })} data-testid={`upload-doc-${k}`} /></label>
              </div>
            );
          })}
          <p className="text-xs text-osk-muted">{tx("Voorwaarden geaccepteerd: versie {v}", { v: p.terms_version })}</p>
        </div>
      </div>
    </div>
  );
}

function Earnings({ lang }) {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/teacher/earnings", { params: month ? { month } : {} }).then((r) => setD(r.data)); }, [month]);
  if (!d) return <Spinner />;
  return (
    <>
      <div className="mb-4 flex items-center gap-3"><span className="osk-label mb-0">{tx("Maand")}</span><input type="month" className="osk-input max-w-[200px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="earnings-month-input" /></div>
      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <Stat label={tx("Gegeven lessen")} value={d.totals.sessions} testid="earnings-sessions" />
        <Stat label={tx("Bruto")} value={money(d.totals.gross)} testid="earnings-gross" />
        <Stat label={tx("Platformcommissie")} value={money(d.totals.commission)} testid="earnings-commission" />
        <Stat label={tx("Netto uitbetaling")} value={money(d.totals.net)} testid="earnings-net" />
      </div>
      <p className="mb-3 text-xs text-osk-muted">{tx("Alleen lessen die daadwerkelijk zijn gegeven (op basis van aanwezigheid) tellen mee. Uitbetaling gebeurt maandelijks door Onskola.")}</p>
      <Table testid="earnings-table" rows={d.rows} empty={tx("Nog geen gegeven lessen.")} cols={[
        { label: tx("Datum"), render: (r) => fmtFull(r.start, lang) },
        { label: tx("Les"), key: "lesson_title" },
        { label: tx("Bruto"), render: (r) => money(r.gross, r.currency) },
        { label: tx("Commissie"), render: (r) => `${money(r.commission, r.currency)} (${r.rate}%${r.loyalty ? " loyaliteit" : ""})` },
        { label: tx("Netto"), render: (r) => <b>{money(r.net, r.currency)}</b> },
      ]} />
    </>
  );
}

export default function TeacherDashboard() {
  const { user, refresh } = useAuth();
  const { lang } = useI18n();
  const [tab, setTab] = useTab("sessions");
  const approved = user.teacher_status === "approved";
  const tabs = [["sessions", tx("Lessen & aanwezigheid"), CalendarDays], ["lessons", tx("Mijn lessen"), BookOpen], ["profile", tx("Profiel & documenten"), UserCog], ["earnings", tx("Inkomsten"), Wallet]];
  return (
    <DashShell testid="teacher-dashboard" title={tx("Docentendashboard")} tabs={tabs} tab={tab} setTab={setTab}
      sub={approved ? tx("Je account is goedgekeurd.") : tx("Je aanmelding wordt beoordeeld. Upload je documenten in het tabblad Profiel.")}>
      {!approved && <div className="osk-card mb-6 flex items-center gap-3 bg-tile-yellow p-4 text-sm" data-testid="teacher-status-banner"><Clock className="h-5 w-5 text-osk-orange" />{tx("Status:")} <Badge status={user.teacher_status} /></div>}
      {tab === "sessions" && <Sessions lang={lang} />}
      {tab === "lessons" && <Lessons approved={approved} />}
      {tab === "profile" && <Profile refresh={refresh} />}
      {tab === "earnings" && <Earnings lang={lang} />}
    </DashShell>
  );
}
